import { OfferJson } from "../OrderForm";
import Utils from "./Utils";

export enum OfferType {
    OFFERTYPE_BPM = 'bpm',
    OFFERTYPE_FP = 'fp',
}

export enum OfferTariff {
    G11 = 'G11',
    G12 = 'G12',
    G12w = 'G12w',
    C11 = 'C11',
    C12a = 'C12a',
    C12b = 'C12b',
}

interface OfferUiPrice {
    amount: number;
    postfix?: string;
}

export interface OfferUi {
    campaignTitle?: string;
    offerGuarantee?: string;
    layout?: string;
    isHighlighted?: boolean;
    highlightText?: string;
    badge?: string;

    discount?: {
        originalPrice?: string;
        discountedPrice?: string;
    };

    prices?: {
        price1?: OfferUiPrice;
        price2?: OfferUiPrice;
        next1?: OfferUiPrice;
        next2?: OfferUiPrice;
    };

    priceInfo?: {
        text?: string;
    };

    terms?: {
        text?: string;
        url?: string;
    }[];

    benfits?: {
        smallBenefits?: {
            text?: string;
            icon?: string;
            format?: string;
        }[];

        largeBenefits?: {
            title?: string;
            description?: string;
            icon?: string;
        }[];
    };
}

export enum OfferCodeType {
    SINGLE = 'single',
    MULTI = 'multi',
}

export interface OfferCodeItem {
    offer: OfferJson;
    ui: OfferUi;
}

export interface OfferCode {
    code: string;
    validto?: Date;

    campaignTitle: string;
    heroImage: string;
    heroTagline1: string;
    heroTagline2: string;
    supplementText: string;

    items: OfferCodeItem[];
}

export interface OfferTerm {
    title: string;
    url: string;
}

export default class OfferUtils {
    public static getNextPrice400(offer: OfferJson, tariff: OfferTariff, zone?: number): number {
        if (offer.type != OfferType.OFFERTYPE_FP || offer.tariff != tariff) {
            return null;
        }

        if (tariff == OfferTariff.G11) {
            switch (offer.price_warranty) {
                case 24:
                case 36:
                    return 0.739;

                case 60:
                    return 0.729;

                case 120:
                    return 0.719;
            }
        } else if (tariff == OfferTariff.G12) {
            if (zone == 1) {
                switch (offer.price_warranty) {
                    case 24:
                        return 0.7608;

                    case 36:
                        return 0.7608;

                    case 60:
                        return 0.7505;

                    case 120:
                        return 0.7402;
                }
            } else if (zone == 2) {
                switch (offer.price_warranty) {
                    case 24:
                        return 0.6811;

                    case 36:
                        return 0.6811;

                    case 60:
                        return 0.6719;

                    case 120:
                        return 0.6627;
                }
            }
        } else if (tariff == OfferTariff.G12w) {
            if (zone == 1) {
                switch (offer.price_warranty) {
                    case 24:
                        return 0.8545;

                    case 36:
                        return 0.8545;

                    case 60:
                        return 0.8429;

                    case 120:
                        return 0.8313;
                }
            } else if (zone == 2) {
                switch (offer.price_warranty) {
                    case 24:
                        return 0.6521;

                    case 36:
                        return 0.6521;

                    case 60:
                        return 0.6433;

                    case 120:
                        return 0.6345;
                }
            }
        } else {
            return null;
        }
    }

    public static getOfferTerms(offer: OfferJson, codeitem: OfferCodeItem = null): OfferTerm[] {
        const result = [];

        if (codeitem) {
            const list = codeitem.ui.terms.filter(v => v.text && v.url);

            for (const item of list) {
                result.push({
                    title: item.text,
                    url: item.url,
                });
            }
        } else {
            if (offer.id >= 10000) {
                return offer.terms;
            } else {
                switch (offer.type) {
                    case OfferType.OFFERTYPE_BPM:
                        result.push({
                            title: 'Regulamin Produktu',
                            url: '/files/dokumenty/2025/01/B2C.Regulamin.BP.010125.pdf',
                        });
                        break;

                    case OfferType.OFFERTYPE_FP:
                        if (offer.id >= 700 && offer.id <= 799) {
                            result.push({
                                title: 'Regulamin Produktu',
                                url: '/files/dokumenty/b2c/2025/B2C.Regulamin.FP.Partner.230425.pdf',
                            });
                        } else {
                            result.push({
                                title: 'Regulamin Produktu',
                                url: '/files/dokumenty/2025/01/B2C.Regulamin.FP.010125.pdf',
                            });
                        }

                        result.push({
                            title: 'Regulamin Promocji',
                            url: '/files/dokumenty/2025/01/B2C.Regulamin.Promo.Tarcza2025.01092025.pdf',
                        });
                        break;
                }

                if (offer.id >= 500 && offer.id <= 699) {
                    result.push({
                        title: 'Regulamin Voucherów Promocyjnych',
                        url: '/files/dokumenty/2025/02/B2C.Regulamin.Voucher.270125.pdf',
                    });
                }
            }
        }

        return result;
    }

    public static getTariffType(tariff: string): string {
        switch (tariff) {
            case OfferTariff.G11:
                return 'Całodobowa';

            case OfferTariff.G12:
                return 'Nocna';

            case OfferTariff.G12w:
                return 'Weekendowa';

            case OfferTariff.C11:
                return 'Całodobowa';

            case OfferTariff.C12a:
                return 'Szczytowa';

            case OfferTariff.C12b:
                return 'Nocna';
        }
    }

    public static getOfferCodeItem(code: OfferCode, offerid: number): OfferCodeItem {
        return code?.items.find(v => v.offer.id == offerid);
    }

    public static async fetchOffers(tariff?: string): Promise<OfferJson[]> {
        const r = await Utils.get('/api/cart/offers');

        if (tariff) {
            return (<OfferJson[]>r.offers).filter(v => v.tariff == tariff);
        } else {
            return (<OfferJson[]>r.offers);
        }
    }
}
